<template>
  <v-card flat tile class="settings-templates-landing">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <settings-templates-help :openSection="openHelpSection"></settings-templates-help>
    </res-dialog>
    <v-card-text>
      <res-alert v-model="alertText"></res-alert>
      <v-container class="pa-0">
        <v-row align="end">
          <v-col class="d-flex justify-end" cols="12" sm="6"></v-col>
          <v-col class="d-flex justify-end" cols="12" sm="6">
            <v-btn rounded elevation="0" class="white--text" color="resGreen" @click.stop="openNewEditSettingsTemplateDialog">
              + New Settings Template
            </v-btn>
            <span class="px-2"></span>
            <div class="help-controls step-down" @click="openHelp(0)">
              <span class="help-label">Help</span>
              <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
            </div>
            <copy-template-dialog :showDialog="showCopyTemplateDialog"
                                  :templateTitle="cloningItem.name"
                                  @confirmCopy="cloneItem"
                                  @cancelCopy="cancelCopyTemplateDialog">
            </copy-template-dialog>
            <new-edit-settings-template-dialog v-if="isNewEditSettingsTemplateDialogOpen"
                                               :showDialog="isNewEditSettingsTemplateDialogOpen"
                                               :maxWidth="1200"
                                               :setWidth="1200"
                                               @settingsTemplateUpdated="refresh"
                                               @cancel="closeNewEditSettingsTemplateDialog">
            </new-edit-settings-template-dialog>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="pa-0 mt-3 template-tabs-container">
        <v-card outlined>
          <v-row>
            <v-col class="d-flex" cols="12" sm="12">
              <v-tabs v-model="tab" class="resman-tabs" active-class="resman-active-tab">
                <v-tabs-slider color="resBlue darken-1"></v-tabs-slider>
                <v-tab v-for="item in tabs"
                       :key="item.id" :class="item.class">
                  {{ item.text }}
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
      <res-data-table :headers="headers"
                      :items="templateItems"
                      item-key="id"
                      :sort-by="sortBy"
                      :sort-desc.sync="sortDescending"
                      :slotOptions="['lastModifiedTimestamp', 'action']"
                      :loading="currentLoading">

        <template v-slot:[`item.lastModifiedTimestamp`]="{ item }">
          {{ localizeDate(item.lastModifiedTimestamp) }}
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip bottom v-if="templateCanBeEdited(item.isArchived, true)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn :to="{ name: 'EditSettingsTemplate', params: { settingsTemplateId: item.settingsTemplateId }}"
                       v-bind="attrs" v-on="on" icon color="var(--v-resBlue-darken1)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-menu offset-y close-on-click>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="var(--v-resBlue-darken1)">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-if="templateCanBeCopied(item.isArchived)" @click="openCopyTemplateDialog(item)">
                  <v-list-item-title>Copy</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item v-if="templateCanBeArchived(item.isArchived)" @click="archiveItem(item, 'true')">
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="templateCanBeUnArchived(item.isArchived)" @click="archiveItem(item, 'false')">
                  <v-list-item-title>Unarchive</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </res-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
  import ResDataTable from '@/components/base/ResDataTable.vue';
  import NewEditSettingsTemplateDialog from '../../budgets/dialogs/NewEditSettingsTemplateDialog.vue';
  import Localizable from '@/mixins/Localizable.js';
  import ResDialog from '@/components/base/ResDialog.vue';
  import CopyTemplateDialog from '../dialogs/CopyTemplateDialog.vue';
  import ResAlert from '@/components/base/ResAlert.vue';

  import HelpableMixin from '@/mixins/Helpable.js';
  import SettingsTemplatesHelp from '@/components/help/SettingsTemplatesHelp.vue';

  import settingsTemplateService from '@/services/settings-template'

  import { mapGetters, mapActions } from 'vuex'
  
  export default {
    name: 'SettingsTemplatesLanding',
    components: {
      ResAlert,
      ResDataTable,
      NewEditSettingsTemplateDialog,
      CopyTemplateDialog,
      SettingsTemplatesHelp,
      ResDialog
    },
    mixins: [
      Localizable,
      HelpableMixin
    ],
    data: function () {
      return {
        alertText: {},
        templates: [],
        tab: null,
        sortBy: "name",
        sortDescending: false,
        isNewEditSettingsTemplateDialogOpen: false,
        showCopyTemplateDialog: false,
        cloningItem: {}
      };
    },
    computed: {
      ...mapGetters(['currentLoading']),
      headers() {
        return this.templates.length > 0 ? [
          { text: "Settings Template", value: "name", sortable: true, filterable: false },
          { text: "Created By", value: "createdByUserName", sortable: true, filterable: false },
          { text: "Last Modified By", value: "lastModifiedByUserName", sortable: true, filterable: false },
          { text: "Last Modified", value: "lastModifiedTimestamp", sortable: true, filterable: false },
          { text: "Actions", value: "action", sortable: false, filterable: false }
        ] : [];
      },
      allTemplatesNonArchived() {
        let temp = this.templates;
        temp = temp.filter((item) => {
          return (!item.isArchived);
        });
        return temp;
      },
      archivedTemplates() {
        let temp = this.templates;
        temp = temp.filter((item) => {
          return (item.isArchived);
        });
        return temp;
      },
      tabs() {
        return [
          { id: 1, text: 'VIEW ALL (' + this.allTemplatesNonArchived.length + ')', class: '' },
          { id: 2, text: 'ARCHIVED (' + this.archivedTemplates.length + ')', class: 'resman-right-tab' },
        ];
      },
      templateItems() {
        switch (this.tab) {
          case 0:
            return this.allTemplatesNonArchived;
          case 1:
            return this.archivedTemplates;
          default:
            return this.allTemplatesNonArchived;
        }
      }
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      async loadTemplates() {
        this.changeLoading(true);
        let data = await settingsTemplateService.getAll(true);
        if (data && data.data) {
          this.templates = data.data;
        } else {
          this.templates = [];
        }
        this.changeLoading(false);
      },
      openCopyTemplateDialog(template) {
        this.cloningItem = template;
        this.showCopyTemplateDialog = true;
      },
      cancelCopyTemplateDialog() {
        this.cloningItem = {};
        this.showCopyTemplateDialog = false;
      },
      async cloneItem() {
        if (!this.cloningItem) {
          this.cancelCopyTemplateDialog();
          return;
        }
        this.changeLoading(true);
        var succeeded = await settingsTemplateService.copyTemplate(this.cloningItem.settingsTemplateId);
        if (succeeded) {
          await this.loadTemplates();
        } else {
          this.changeLoading(false);
        }
        this.cancelCopyTemplateDialog();
      },
      templateCanBeEdited(isArchived, userCanEdit) {
        return !isArchived && userCanEdit;
      },
      templateCanBeCopied(isArchived) {
        return !isArchived;
      },
      templateCanBeArchived(isArchived) {
        return !isArchived;
      },
      templateCanBeUnArchived (isArchived) {
        return isArchived;
      },
      async archiveItem(item, newIsArchived) {
        this.changeLoading(true);
        var succeeded = await settingsTemplateService.updateIsArchived(item.settingsTemplateId, newIsArchived, item.lastModifiedTimestamp);
        if (succeeded) {
          await this.loadTemplates();
        } else {
          this.changeLoading(false);
        }
      },
      openNewEditSettingsTemplateDialog() {
        this.isNewEditSettingsTemplateDialogOpen = true;
      },
      closeNewEditSettingsTemplateDialog() {
        this.isNewEditSettingsTemplateDialogOpen = false;
      },
      refresh() {
        this.closeNewEditSettingsTemplateDialog();
        this.loadTemplates();
      }
    },
    async mounted() {
      await this.loadTemplates();
    },
    watch: {

    }
  };
</script>

<style lang="scss" scoped>
  .resman-tab {
    color: var(--v-resGrey-darken1) !important;
    font-weight: normal !important;
    font-size: 18px !important;
  }

  .resman-tab.resman-active-tab {
    font-weight: 500 !important;
    color: var(--v-resBlue-darken1) !important;
  }

  .v-data-table-header th.resGrey-background {
    background-color: var(--v-resGrey-lighten3) !important;
  }

  #IncludeArchived { 
      ::v-deep.v-input__slot {
        align-items: flex-end;
        margin: 0;
      }
  }
  .help-controls {
    float: right;
    height:36px;
    border-radius: 5px;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }



    .resman-tab.resman-active-tab {
      font-weight: 500 !important;
      color: var(--v-resBlue-darken1) !important;
    }

  .v-data-table-header th.resGrey-background {
    background-color: var(--v-resGrey-lighten3) !important;
  }

  .budget-landing .table-container::v-deep .v-data-table th.res-data-table-actions {
    min-width: 55px !important;
  }

  .budget-tabs-container {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .resman-right-tab {
    margin-left: auto;
  }

  .help-controls {
    float: right;
    border-radius: 5px;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }
</style>

<!-- Non-scoped needed for dynamic table row classes -->
<style lang="scss">
  .v-data-table tr.archived-template {
    color: lightgray;
  }
</style>
